import axios, { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { CommonRootState } from '../combineReducers';
import { IOrganization } from '../interfaces/organization';
import { sendError } from '../sendError';
import { ThunkAction } from 'redux-thunk';
import { isComplianceRegionUS } from '../helpers/complianceRegion';

export const fetchOrganizationRequest = () =>
  ({
    type: 'FETCH_ORGANIZATION_REQUEST'
  } as const);

export const fetchOrganizationSuccess = (payload: IOrganization, ssqId: string) => {
  Cookies.set('AionCurrentOrganizationId', payload.id, {
    sameSite: 'lax'
  });
  return {
    type: 'FETCH_ORGANIZATION_SUCCESS',
    payload,
    ssqId
  } as const;
};

export const fetchOrganizationFailure = (error: AxiosError | Error) => {
  sendError(error);
  return {
    type: 'FETCH_ORGANIZATION_FAILURE',
    error
  } as const;
};

const shouldFetchOrganization = (state: CommonRootState, organizationId: string) => {
  const {
    organization: { isFetching, fetchError, organization }
  } = state;

  return !isFetching && !fetchError && (!organization || (organization && organization.id !== organizationId));
};

const shouldRefetchOrganization = (state: CommonRootState) => {
  const {
    organization: { isFetching }
  } = state;

  return !isFetching;
};

const fetchOrganization = (
  organizationId: string
): ThunkAction<void, CommonRootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchOrganizationRequest());
    const response = await axios.get<IOrganization>(
      `/api/v3.00/organizations(${organizationId})?exposeLegacyId&includeClientFeatures=true&includeRegistrationNumbers=true`
    );
    const { features } = response.data;
    const isStandard = isComplianceRegionUS(features);
    dispatch(fetchOrganizationSuccess({ ...response.data, isStandard }, response.headers['x-ssqcompanyid']));
  } catch (error) {
    dispatch(fetchOrganizationFailure(error));
  }
};

export const fetchOrganizationIfNeeded = (
  organizationId: string
): ThunkAction<void, CommonRootState, null, Actions> => (dispatch, getState) => {
  if (shouldFetchOrganization(getState(), organizationId)) {
    dispatch(fetchOrganization(organizationId));
  }
};

export const refetchOrganizationIfNeeded = (
  organizationId: string
): ThunkAction<void, CommonRootState, null, Actions> => (dispatch, getState) => {
  if (shouldRefetchOrganization(getState())) {
    return dispatch(fetchOrganization(organizationId));
  }
};

export type Actions =
  | ReturnType<typeof fetchOrganizationRequest>
  | ReturnType<typeof fetchOrganizationSuccess>
  | ReturnType<typeof fetchOrganizationFailure>;
